import { translationService, type Language } from './translations';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import disclaimerData from './assets/disclaimer/customer.json';

// Global variables
let companyInfo: CompanyInfo | null = null;
let taxRates: Map<string, TaxRate> = new Map();

// ✅ Initialize input immediately (not in init())
let input: InputFiles = {
  customerDisclaimer: [],
  taxDisclaimer: []
};

enum ExchangeRateHeader {
  MID_RATE="Srednji za devize",
  DATE="Datum primjene"
}

enum ExchangeDateHeader {
  DATE="Datum primjene"
}

interface ExchangeRateSelector {
  dateHeader: ExchangeDateHeader,
  rateHeader: ExchangeRateHeader
}

interface CompanyInfo {
  name: string;
  address: string;
  email: string;
  oib: string;
  mbs: string;
  euid: string;
}

interface TaxRate {
  country: string;
  shippingTax: number;
  productTax: number;
}

interface InputFiles {
  invoiceFile?: File,
  exchangeRateFile?: File,
  taxFile?: File,
  exchangeRateSelector?: ExchangeRateSelector,  // Add this line
  customerDisclaimer: {customer: string}[],
  taxDisclaimer: Map<string, string>[]
}

interface ConversionData {
  rate: number
  valuta?: string
}

interface Order {
  status: "Shipped" | "Cancelled";
  orderId: string;
  isBusinessOrder: string;
  isAmazonVine?: boolean;
  buyerName: string;
  buyerEmail: string;
  purchaseDate: string;
  country: string;
  shippingAddress: string;
  items: { name: string; quantity: number; price: number; asin: string }[];
  totalItemPrice: number;
  totalItemTax: number;
  totalShippingCost: number;
  totalShippingTax: number;
  itemDiscount: number;
  shippingDiscount: number;
  subtotal: number;
  totalTax: number;
  grandTotal: number;
  conversion?: ConversionData;
}

async function invoiceGenFactory(input: InputFiles): Promise<InvoiceGenerator> {
  const instance = new InvoiceGenerator(input);
  await instance.init();
  return instance;
}

class InvoiceGenerator {
  orders: Order[] = [];
  public exchangeData: any[] = [];
  public invoiceData: any[] = [];
  private abortController: AbortController = new AbortController();

  constructor(private input: InputFiles) {  
  }

  async init(): Promise<void> {
    await Promise.all([this.parseInvoiceFile(), this.parseExchangeRate()]);
    console.log("Invoice and Exchange rate data loaded successfully!");
  }

  async parseInvoiceFile(): Promise<void> {
    if (!this.input.invoiceFile) return;

    const reader = new FileReader();
    const filePromise = new Promise<void>((resolve, reject) => {
      reader.onload = async (e: ProgressEvent<FileReader>) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          this.invoiceData = XLSX.utils.sheet_to_json(sheet) as any[];

          console.log("Invoice Data:", this.invoiceData);
          resolve();
        } catch (error) {
          console.error("Error parsing invoice file:", error);
          reject(error);
        }
      };
    });

    reader.readAsArrayBuffer(this.input.invoiceFile);
    return filePromise;
  }

  async parseExchangeRate(): Promise<void> {
    if (!this.input.exchangeRateFile) return;

    const reader = new FileReader();
    const filePromise = new Promise<void>((resolve, reject) => {
      reader.onload = (e: ProgressEvent<FileReader>) => {
        try {
          const jsonData = JSON.parse(e.target?.result as string);
          this.exchangeData = jsonData;
          console.log("Exchange Rate Data:", this.exchangeData);
          resolve();
        } catch (error) {
          console.error("Error parsing exchange rate file:", error);
          reject(error);
        }
      };
    });

    reader.readAsText(this.input.exchangeRateFile);
    return filePromise;
  }

  async process() {
    this.parseInvoiceData(this.invoiceData as any[]);
    await this.generateIndividualPDFs();
    console.log(`Generated ${this.orders.length} individual invoices.`);
  }

  formatDate(dateString: string): string {
    if (!dateString) return "";

    if (dateString.includes(".")) {
        const [day, month, year] = dateString.split('.').slice(0, 3).map(Number);
        return new Date(year, month - 1, day).toLocaleDateString('de-DE'); 
    }

    const date = new Date(dateString);
    
    if (isNaN(date.getTime())) {
        console.error("Invalid date format:", dateString);
        return "";
    }

    return date.toLocaleDateString('de-DE');
  }

  parseInvoiceData(data: any[]): void {
    const groupedOrders = data.reduce((acc: Record<string, Order>, row: any) => {
      const orderId = row['amazon-order-id'];
      const item = {
        name: row['product-name'],
        asin: row['asin'] || 'N/A',
        quantity: +row['quantity'] || 0,
        price: +row['item-price'] || 0,
      };

      const orderStatus = row['order-status']?.toLowerCase();
      if (orderStatus === 'cancelled' || item.quantity==0) {
        return acc;
      }

      const shippingCost = +row['shipping-price'] || 0;
      const shippingDiscount = +row['ship-promotion-discount'] || 0;
      const itemDiscount = +row['item-promotion-discount'] || 0;

      if (!acc[orderId]) {
        acc[orderId] = {
          status: row["order-status"],
          orderId,
          isBusinessOrder: row['is-business-order'],
          isAmazonVine: false,
          buyerName: row['buyer-company-name'],
          buyerEmail: row['buyer-email'] || 'Not Provided',
          purchaseDate: row['purchase-date'] || 'Not Provided',
          country: row['ship-country'],
          shippingAddress: `${row['ship-address-1'] || ''} ${row['ship-address-2'] || ''}\n${translationService.ts('invoice.city')}: ${row['ship-city'] || ''}\n${translationService.ts('invoice.state')}: ${row['ship-state'] || ''}\n${translationService.ts('invoice.postal_code')}: ${row['ship-postal-code'] || ''}\n${translationService.ts('invoice.country')}: ${row['ship-country'] || ''}`.trim(),
          items: [],
          totalItemPrice: 0,
          totalItemTax: 0,
          totalShippingCost: 0,
          totalShippingTax: 0,
          itemDiscount: 0,
          shippingDiscount: 0,
          subtotal: 0,
          totalTax: 0,
          grandTotal: 0,
        };
      }

      acc[orderId].items.push(item);
      acc[orderId].totalItemPrice += item.price * item.quantity;
      acc[orderId].totalShippingCost += shippingCost;
      acc[orderId].itemDiscount += itemDiscount;
      acc[orderId].shippingDiscount += shippingDiscount;

      acc[orderId].subtotal =
        acc[orderId].totalItemPrice + acc[orderId].totalShippingCost -
        (acc[orderId].itemDiscount + acc[orderId].shippingDiscount);

      this.calculateTax(acc[orderId])
      this.checkIfAmazonVine(acc[orderId])
      this.setCustomerName(acc[orderId])
      return acc;
    }, {});

    this.orders = Object.values(groupedOrders);
  }

  setCustomerName(order: Order) {
    if (!order.buyerName && order.isAmazonVine) {
      order.buyerName = "Amazon Vine " + translationService.ts('invoice.end_customer')
    }
    else {
      order.buyerName = order.buyerName ?? translationService.ts('invoice.end_customer')
    }
  }

  checkIfAmazonVine(order: Order) {
    order.isAmazonVine = order.grandTotal == 0
  }

  calculateTax(order: Order) {
    // Find tax rates from tax.json
    const taxConfig = this.input.taxDisclaimer?.find(tax => tax.get("country") == order.country);
    let productTaxRate = 0;
    let shippingTaxRate = 0;
    
    if (taxConfig) {
      productTaxRate = parseFloat(taxConfig.get("productTax") || "0");
      shippingTaxRate = parseFloat(taxConfig.get("shippingTax") || "0");
      const itemsAfterDiscount = order.totalItemPrice - order.itemDiscount;
      order.totalItemTax = productTaxRate * Math.max(0, itemsAfterDiscount);
      const shippingAfterDiscount = order.totalShippingCost - order.shippingDiscount;
      order.totalShippingTax = shippingTaxRate * Math.max(0, shippingAfterDiscount);
    }
    
    order.totalTax = order.totalItemTax + order.totalShippingTax;
    order.grandTotal = order.subtotal + order.totalTax;
  }

  async generateIndividualPDFs(): Promise<void> {
    const totalCount = this.orders.length;
    let generatedCount = 0;

    for (const order of this.orders) {
      if (this.abortController.signal.aborted) {
        console.log("Invoice generation cancelled by user.");
        return;
      }
      
      await new Promise((resolve) => {
        setTimeout(() => {
          const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            compress: true,
            putOnlyUsedFonts: true
          });
          
          const pageWidth = doc.internal.pageSize.width;
          const pageHeight = doc.internal.pageSize.height;
          const margin = 10;
          const tableWidth = pageWidth - (2 * margin);
          
          // Calculate footer space needed
          const footerSpace = this.calculateFooterHeight(doc, order);
          
          const startY = this.addHeader(doc, order);

          this.convertValuta(order)
          const valuta = "EUR"

          // Products table with white background and black borders
          autoTable(doc, {
            startY: startY,
            margin: { left: margin, right: margin, bottom: footerSpace },
            tableWidth: tableWidth,
            head: [[
              translationService.ts('invoice.product_name'),
              translationService.ts('invoice.asin'),
              translationService.ts('invoice.quantity'),
              `${translationService.ts('invoice.price')} (${valuta})`
            ]],
            body: order.items.map((item) => [
              item.name,
              item.asin, // ✅ Add ASIN value
              item.quantity.toString(),
              item.price.toFixed(2),
            ]),
            columnStyles: {
              0: { cellWidth: 'auto' }, // Product name - flexible
              1: { cellWidth: 30 },     // ASIN - fixed width
              2: { cellWidth: 20 },     // Quantity
              3: { cellWidth: 30, halign: 'right' }, // Price - right aligned
            },
            styles: {
              font: 'helvetica',
              fontSize: 10,
              fillColor: [255, 255, 255],
              textColor: [0, 0, 0],
              lineColor: [0, 0, 0],
              lineWidth: 0.1
            },
            headStyles: {
              fillColor: [240, 240, 240],
              textColor: [0, 0, 0],
              fontStyle: 'bold',
              lineColor: [0, 0, 0],
              lineWidth: 0.1
            },
            alternateRowStyles: {
              fillColor: [255, 255, 255]
            }
          });

          const tableY = (doc as any).lastAutoTable.finalY || 140;

          // Build summary table body dynamically
          const summaryBody: any[] = [
            [
              translationService.ts('invoice.total_item_price'),
              { content: order.totalItemPrice.toFixed(2), styles: { halign: 'right' } }
            ],
            [
              translationService.ts('invoice.total_shipping_cost'),
              { content: order.totalShippingCost.toFixed(2), styles: { halign: 'right' } }
            ]
          ];

          // Add discounts if they exist
          if (order.itemDiscount > 0) {
            summaryBody.push([
              translationService.ts('invoice.item_discounts'),
              { content: `-${order.itemDiscount.toFixed(2)}`, styles: { halign: 'right' } }
            ]);
          }

          if (order.shippingDiscount > 0) {
            summaryBody.push([
              translationService.ts('invoice.shipping_discounts'),
              { content: `-${order.shippingDiscount.toFixed(2)}`, styles: { halign: 'right' } }
            ]);
          }

          // Subtotal row
          summaryBody.push([{
            content: translationService.ts('invoice.subtotal'),
            colSpan: 2,
            styles: { 
              halign: 'left', 
              fontStyle: 'bold', 
              fillColor: [245, 245, 245],
              textColor: [0, 0, 0],
              lineColor: [0, 0, 0],
              lineWidth: 0.2
            }
          }]);

          summaryBody.push([{
            content: order.subtotal.toFixed(2),
            colSpan: 2,
            styles: { 
              halign: 'right', 
              fillColor: [245, 245, 245],
              textColor: [0, 0, 0],
              fontStyle: 'bold',
              lineColor: [0, 0, 0],
              lineWidth: 0.2
            }
          }]);

          // Add tax rows if tax exists
          if (order.totalItemTax > 0 || order.totalShippingTax > 0) {
            if (order.totalItemTax > 0) {
              summaryBody.push([
                translationService.ts('invoice.product_tax'),
                { content: order.totalItemTax.toFixed(2), styles: { halign: 'right' } }]
              );
            }

            if (order.totalShippingTax > 0) {
              summaryBody.push([
                translationService.ts('invoice.shipping_tax'),
                { content: order.totalShippingTax.toFixed(2), styles: { halign: 'right' } }]
              );
            }

            // Total tax row
            summaryBody.push([
              translationService.ts('invoice.total_tax'),
              { 
                content: order.totalTax.toFixed(2), 
                styles: { halign: 'right', fontStyle: 'bold' } 
              }
            ]);
          }

          // Grand total row
          summaryBody.push([{
            content: translationService.ts('invoice.total_amount'),
            colSpan: 2,
            styles: { 
              halign: 'center', 
              fontStyle: 'bold', 
              fontSize: 12, 
              fillColor: [230, 255, 230],
              textColor: [0, 0, 0],
              lineColor: [0, 0, 0],
              lineWidth: 0.3
            }
          }]);

          summaryBody.push([{
            content: order.grandTotal.toFixed(2),
            colSpan: 2,
            styles: { 
              halign: 'right', 
              fontSize: 12, 
              fillColor: [230, 255, 230],
              textColor: [0, 0, 0],
              fontStyle: 'bold',
              lineColor: [0, 0, 0],
              lineWidth: 0.3
            }
          }]);

          // Summary table with white background and black borders
          autoTable(doc, {
            startY: tableY + 10,
            margin: { left: margin, right: margin, bottom: footerSpace },
            tableWidth: tableWidth,
            head: [[
              translationService.ts('invoice.description'),
              `${translationService.ts('invoice.amount')} (${valuta})`
            ]],
            body: summaryBody,
            columnStyles: {
              0: { cellWidth: 'auto' },
              1: { cellWidth: 50, halign: 'right' },
            },
            styles: { 
              fontSize: 10,
              font: 'helvetica',
              fillColor: [255, 255, 255],
              textColor: [0, 0, 0],
              lineColor: [0, 0, 0],
              lineWidth: 0.1
            },
            headStyles: {
              fillColor: [240, 240, 240],
              textColor: [0, 0, 0],
              fontStyle: 'bold',
              lineColor: [0, 0, 0],
              lineWidth: 0.1
            },
            alternateRowStyles: {
              fillColor: [255, 255, 255]
            }
          });

          this.addFooter(order, doc);

          const fileName = `Invoice_${order.orderId}_${this.formatDate(order.purchaseDate)}.pdf`;
          doc.save(fileName);

          generatedCount++;
          this.updateProgress(generatedCount, totalCount);

          resolve(true);
        }, 300);
      });
    }
  }

  // Helper method to calculate footer height
  calculateFooterHeight(doc: jsPDF, order: Order): number {
    const pageWidth = doc.internal.pageSize.width;
    let totalLines = 0;
    
    // Exchange rate info
    if (order.conversion?.rate) {
      const rate = (1/order.conversion.rate).toFixed(4);
      const valuta = order.conversion?.valuta;
      
      const line1 = translationService.ts('footer.exchange_rate_info').replace('{currency}', valuta || '');
      const line2 = translationService.ts('footer.exchange_rate_date')
        .replace('{date}', this.formatDate(order.purchaseDate))
        .replace('{currency}', valuta || '')
        .replace('{rate}', rate);
      
      const maxWidth = pageWidth - 20;
      doc.setFontSize(9);
      
      totalLines += doc.splitTextToSize(line1, maxWidth).length;
      totalLines += doc.splitTextToSize(line2, maxWidth).length;
    }
    
    // Company info
    if (companyInfo) {
      totalLines += 3;
    }
    
    // Disclaimers
    doc.setFontSize(8);
    const maxWidth = pageWidth - 20;
    const disclaimerLines = this.getDisclaimer(order);
    
    disclaimerLines.forEach((line) => {
      if (line === '') {
        totalLines += 0.5;
      } else {
        totalLines += doc.splitTextToSize(line, maxWidth).length;
      }
    });
    
    totalLines += 2; // Separator + padding
    
    return totalLines * 5 + 10; // 5mm per line + 10mm bottom padding
  }

  private updateProgress(current: number, total: number): void {
    const progressCountEl = document.getElementById('progressCount');
    const totalCountEl = document.getElementById('totalCount');
    if (progressCountEl) progressCountEl.textContent = current.toString();
    if (totalCountEl) totalCountEl.textContent = total.toString();
  }

  public abort(): void {
    this.abortController.abort();
  }

  public resetAbort(): void {
    this.abortController = new AbortController();
  }

  convertValuta(order: Order): Order{
    if (!this.input.exchangeRateFile) {
      return order;
    }
    let selector = this.input.exchangeRateSelector;
    let rate = this.exchangeData.find((p: any) => {
      return this.formatDate(p[selector?.dateHeader as ExchangeDateHeader]).trim() === this.formatDate(order.purchaseDate).trim()
    })
    if (!rate) {
      throw Error("ExchangeRate not existent")
    }
    let exchangeRate = this.convertToNumber(rate[selector?.rateHeader as ExchangeRateHeader]);

    if (exchangeRate) {
      order.totalItemPrice *= 1/exchangeRate
      order.totalShippingCost *= 1/exchangeRate
      order.itemDiscount *= 1/exchangeRate
      order.shippingDiscount *= 1/exchangeRate
      order.subtotal *= 1/exchangeRate
      order.grandTotal *= 1/exchangeRate
      order.items = order.items.map((item) => ({...item, price: item.price*(1/exchangeRate)}))
      order.conversion = {
        rate: exchangeRate,
        valuta: rate["Valuta"] as string
      }
    }
    else {
      throw Error("ExchangeRate not existent")
    }
    return order
  }

  addHeader(doc: jsPDF, order: Order): number {
    if (!companyInfo) return 50;

    // Set font to support Croatian characters
    doc.setFont('helvetica');
    
    doc.setFontSize(16);
    doc.text(translationService.ts('invoice.title') + ' ' + order.orderId, 10, 10);
    doc.setFontSize(10);
    
    const companyText = `${companyInfo.name}\n${companyInfo.address}\nEmail: ${companyInfo.email}`;
    companyText.split("\n").forEach((line, index) => {
      doc.text(line, 10, 20 + (index * 5));
    });

    let startY = 50;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(translationService.ts('invoice.recipient_details'), 10, startY);
    startY += 7
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    let customerInformation = `${translationService.ts('invoice.name')}: ${order.buyerName}\n`;
    customerInformation += order.shippingAddress + "\n"; 
    customerInformation += `${translationService.ts('invoice.purchase_date')}: ${order.purchaseDate}`;
    customerInformation.trim().split("\n").forEach(line => {
      doc.text(line, 10, startY);
      startY += 5;
    });

    return startY;
  }

  addFooter(order: Order, doc: jsPDF): void {
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 10;
    
    // Get disclaimer lines
    const disclaimerLines = this.getDisclaimer(order);
    
    // Calculate needed lines for footer content
    let totalLines = 0;
    
    // 1. Exchange rate info (if exists)
    if (order.conversion?.rate) {
      const rate = (1/order.conversion.rate).toFixed(4);
      const valuta = order.conversion?.valuta;
      
      const line1 = translationService.ts('footer.exchange_rate_info').replace('{currency}', valuta || '');
      const line2 = translationService.ts('footer.exchange_rate_date')
        .replace('{date}', this.formatDate(order.purchaseDate))
        .replace('{currency}', valuta || '')
        .replace('{rate}', rate);
      
      const maxWidth = pageWidth - 20;
      doc.setFontSize(9);
      
      const wrappedLine1 = doc.splitTextToSize(line1, maxWidth);
      const wrappedLine2 = doc.splitTextToSize(line2, maxWidth);
      
      totalLines += wrappedLine1.length + wrappedLine2.length;
    }
    
    // 2. Company info (3 lines)
    if (companyInfo) {
      totalLines += 3;
    }
    
    // 3. Disclaimers
    doc.setFontSize(8);
    const maxWidth = pageWidth - 20;
    disclaimerLines.forEach((line) => {
      if (line === '') {
        totalLines += 0.5; // Half line for spacing
      } else {
        const wrappedLines = doc.splitTextToSize(line, maxWidth);
        totalLines += wrappedLines.length;
      }
    });
    
    // Add extra space for separator and padding
    totalLines += 2;
    
    // Calculate starting Y position (line height = 5mm for size 9, 4.5mm for size 8)
    const lineHeight = 5;
    const footerHeight = totalLines * lineHeight;
    let footerY = pageHeight - footerHeight - 5; // 5mm bottom padding

    // Draw separator line
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);

    // Exchange rate info
    if (order.conversion?.rate) {
      const rate = (1/order.conversion.rate).toFixed(4);
      const valuta = order.conversion?.valuta;
      
      const line1 = translationService.ts('footer.exchange_rate_info').replace('{currency}', valuta || '');
      const line2 = translationService.ts('footer.exchange_rate_date')
        .replace('{date}', this.formatDate(order.purchaseDate))
        .replace('{currency}', valuta || '')
        .replace('{rate}', rate);
      
      const maxWidth = pageWidth - 20;
      
      doc.splitTextToSize(line1, maxWidth).forEach((line: string) => {
        doc.text(line, margin, footerY);
        footerY += 5;
      });
      
      doc.splitTextToSize(line2, maxWidth).forEach((line: string) => {
        doc.text(line, margin, footerY);
        footerY += 5;
      });
      
      footerY += 3; // Extra spacing
    }

    // Company info
    if (companyInfo) {
      doc.setFontSize(8);
      doc.text(`${translationService.ts('footer.issued_by')} ${companyInfo.name}`, margin, footerY);
      footerY += 4;
      doc.text(`OIB: ${companyInfo.oib} | MBS: ${companyInfo.mbs} | EUID: ${companyInfo.euid}`, margin, footerY);
      footerY += 6;
    }

    // Disclaimers
    doc.setFontSize(8);
    
    disclaimerLines.forEach((line) => {
      if (line === '') {
        footerY += 3; // Spacing
        return;
      }
      
      const wrappedLines = doc.splitTextToSize(line, maxWidth);
      wrappedLines.forEach((wrappedLine: string) => {
        doc.text(wrappedLine, margin, footerY);
        footerY += 4.5;
      });
    });
  }
  
  convertToNumber(str: string): number {
    return parseFloat(str.replace(',', '.'));
  }

  getDisclaimer(order: Order): string[] {
    const country = order.country;
    
    // Get country-specific disclaimer as array
    const countryDisclaimer = translationService.ta(`disclaimers.${country}`);
    const lines: string[] = [...countryDisclaimer];
    
    // Add Vine disclaimer if applicable
    if (order.isAmazonVine) {
      const vineDisclaimer = translationService.ta('disclaimers.vine');
      lines.push(''); // Empty line as separator
      lines.push(...vineDisclaimer);
    }
    
    return lines;
  }
}

document.getElementById('companyFileInput')?.addEventListener('change', event => {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        companyInfo = JSON.parse(e.target?.result as string);
        console.log("✅ Company info loaded from file:", companyInfo);
      } catch (error) {
        console.error("❌ Error parsing company JSON:", error);
      }
    };
    reader.readAsText(file);
  }
});

document.getElementById('taxFileInput')?.addEventListener('change', async (event) => {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        const taxData = JSON.parse(e.target?.result as string);
        
        console.log("📊 Loaded tax data:", taxData);
        console.log("📊 Is array?", Array.isArray(taxData));
        
        if (Array.isArray(taxData)) {
          // Update taxRates Map
          taxRates = new Map(
            taxData.map((rate: TaxRate) => [rate.country, rate])
          );
          
          // ✅ Update input.taxDisclaimer
          input.taxDisclaimer = taxData.map((rate: any) => {
            const map = new Map<string, string>();
            for (const [key, value] of Object.entries(rate)) {
              map.set(key, String(value));
            }
            return map;
          });
          
          console.log("✅ Tax rates loaded from file:", Array.from(taxRates.keys()));
          console.log("📊 Sample tax config:", input.taxDisclaimer[0]);
        } else {
          console.error("❌ Tax file must be an array of objects. Received:", typeof taxData);
        }
      } catch (error) {
        console.error("❌ Error parsing tax JSON:", error);
      }
    };
    reader.readAsText(file);
  }
});

document.getElementById('invoiceFileInput')?.addEventListener('change', event => {
  input.invoiceFile = (event.target as HTMLInputElement).files?.[0];
  console.log("✅ Invoice file selected:", input.invoiceFile?.name);
});

document.getElementById('exchangeRateFileInput')?.addEventListener('change', event => {
  input.exchangeRateFile = (event.target as HTMLInputElement).files?.[0];
  console.log("✅ Exchange rate file selected:", input.exchangeRateFile?.name);
});

let currentGenerator: InvoiceGenerator | null = null;

function changeLanguage() {
  const select = document.getElementById('languageSelect') as HTMLSelectElement;
  const lang = select.value as Language;
  
  if (!translationService.isInitialized()) {
    console.warn('⚠️ Translations not yet loaded, waiting...');
    setTimeout(() => changeLanguage(), 100);
    return;
  }
  
  translationService.setLanguage(lang);
  updateUILanguage();
}

function updateUILanguage() {
  console.log("🔄 updateUILanguage called");
  console.log("🔍 Translations initialized?", translationService.isInitialized());
  console.log("🔍 Current language:", translationService.getLanguage());
  
  if (!translationService.isInitialized()) {
    console.warn('⚠️ Cannot update UI - translations not loaded');
    return;
  }
  
  const pageTitle = document.getElementById('page-title');
  const labelCompany = document.getElementById('label-upload-company');
  const labelTax = document.getElementById('label-upload-tax');
  const labelInvoice = document.getElementById('label-upload-invoice');
  const labelExchange = document.getElementById('label-upload-exchange-rate');
  const generateBtn = document.getElementById('generateBtn');
  const cancelBtn = document.getElementById('cancelBtn');
  const spinnerText = document.getElementById('spinner-text');

  if (pageTitle) pageTitle.textContent = translationService.ts('invoice.title');
  if (labelCompany) labelCompany.textContent = translationService.ts('common.upload_company');
  if (labelTax) labelTax.textContent = translationService.ts('common.upload_tax');
  if (labelInvoice) labelInvoice.textContent = translationService.ts('common.upload_invoice');
  if (labelExchange) labelExchange.textContent = translationService.ts('common.upload_exchange_rate');
  if (generateBtn) generateBtn.textContent = translationService.ts('common.generate_invoice');
  if (cancelBtn) cancelBtn.textContent = translationService.ts('common.cancel');
  if (spinnerText) spinnerText.innerHTML = translationService.ts('common.generating') + '... <span id="progressCount">0</span>/<span id="totalCount">0</span>';
  
  console.log("✅ UI updated successfully");
}

async function generateInvoice() {
  if (!input.invoiceFile) {
    alert('Please upload an invoice file!');
    return;
  }
  
  const generateBtn = document.getElementById('generateBtn') as HTMLButtonElement;
  const cancelBtn = document.getElementById('cancelBtn') as HTMLButtonElement;
  const spinner = document.getElementById('loadingSpinner') as HTMLElement;
  const fileInputs = document.querySelectorAll('input[type="file"]');

  generateBtn.disabled = true;
  fileInputs.forEach(input => (input as HTMLInputElement).disabled = true);
  spinner.style.display = 'flex';
  cancelBtn.style.display = 'block';
  
  try {
    if (input.exchangeRateFile) {
      input.exchangeRateSelector = {
        dateHeader: ExchangeDateHeader.DATE,
        rateHeader: ExchangeRateHeader.MID_RATE
      }
    }
    const generator = await invoiceGenFactory(input);
    currentGenerator = generator;
    currentGenerator.resetAbort();
    await generator.process();
  } finally {
    generateBtn.disabled = false;
    fileInputs.forEach(input => (input as HTMLInputElement).disabled = false);
    spinner.style.display = 'none';
    cancelBtn.style.display = 'none';
    currentGenerator = null;
  }
}

function cancelGeneration() {
  if (currentGenerator) {
    currentGenerator.abort();
  }
}

async function init() {
  console.log("🚀 Initializing app...");
  
  // Disable language selector during init
  const langSelect = document.getElementById('languageSelect') as HTMLSelectElement;
  if (langSelect) {
    langSelect.disabled = true;
  }
  
  // 1. Load translations first
  await translationService.init();
  
  // 2. Load language from storage
  translationService.loadLanguageFromStorage();
  
  // 3. Set dropdown to current language
  if (langSelect) {
    langSelect.value = translationService.getLanguage();
    langSelect.disabled = false;
  }
  
  // 4. Update UI with translations
  updateUILanguage();
  
  // 5. Load disclaimer files into input object
  input.customerDisclaimer = disclaimerData;
  
  console.log("✅ App initialized successfully!");
  console.log("ℹ️  Please upload company.json via UI");
  
  // ✅ ADD EVENT LISTENERS AFTER INIT
  attachEventListeners();
}

// ✅ NEW FUNCTION - Attach event listeners
function attachEventListeners() {
  console.log("🔗 Attaching event listeners...");
  
  const langSelect = document.getElementById('languageSelect');
  const generateBtn = document.getElementById('generateBtn');
  const cancelBtn = document.getElementById('cancelBtn');
  
  if (langSelect) {
    langSelect.addEventListener('change', changeLanguage);
    console.log("✅ Language selector listener attached");
  }
  
  if (generateBtn) {
    generateBtn.addEventListener('click', generateInvoice);
    console.log("✅ Generate button listener attached");
  }
  
  if (cancelBtn) {
    cancelBtn.addEventListener('click', cancelGeneration);
    console.log("✅ Cancel button listener attached");
  }
}

// Interactive gradient background
function initInteractiveBackground() {
  document.addEventListener('mousemove', (e) => {
    const x = (e.clientX / window.innerWidth) * 100;
    const y = (e.clientY / window.innerHeight) * 100;
    
    // Update CSS custom properties for smooth gradient animation
    document.body.style.setProperty('--mouse-x', `${x}%`);
    document.body.style.setProperty('--mouse-y', `${y}%`);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    init();
    initInteractiveBackground();
  });
} else {
  init();
  initInteractiveBackground();
}